/**
 * Auth slice (SCRUM-1950, RTK bundle 1 of 5): createSlice migration of the
 * legacy `authReducer` switch.
 *
 * Behaviour-preserving. The slice listens for the legacy action type STRINGS
 * through extraReducers, so every dispatcher is unchanged: `authActions.js`
 * (SET_CURRENT_USER, VERIF_PASS, UPDATING_PASS, CURRENT_PERMISSION) and
 * profileSlice's getCurrentProfile thunk, which still dispatches PROFILE_LOADING
 * and GET_PROFILE for this slice as well as its own. State shape and every case
 * are kept verbatim, including the dead `loading` / `profile` / `user` fields.
 * `authReducer.js` re-exports this reducer, so its importers do not change.
 * Pinned by src/tests/unit/rtkAppShell.characterisation.test.js, written against
 * the legacy switch before this migration.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { AuthUserResponse, ProfileResponse, UserResponse } from "../../../../shared/apiTypes";
import type { SitePermission } from "./useCurrentPermission";
import isEmpty from "../../validation/is-empty";

import {
  SET_CURRENT_USER,
  GET_PROFILE,
  PROFILE_LOADING,
  VERIF_PASS,
  UPDATING_PASS,
} from "./userTypes";
import { CURRENT_PERMISSION } from "./types";

/** CURRENT_PERMISSION's payload, as setCurrentPermission builds it. */
type CurrentPermissionPayload = { site_id?: string; permissions?: SitePermission[] } | null | undefined;

type AuthState = {
  isAuthenticated: boolean;
  /** `{ user }` from the login / `/api/users/me` bootstrap, or `{}` when signed out. */
  userconnected: Partial<AuthUserResponse>;
  user: Partial<UserResponse>;
  profile: ProfileResponse;
  loading: boolean;
  verifPass: boolean;
  /** `""` until a site's permissions load, `null` when the user has none for it (SCRUM-856). */
  current_permission: SitePermission | "" | null;
};

const initialState: AuthState = {
  isAuthenticated: false,
  userconnected: {},
  user: {},
  profile: null,
  loading: false,
  verifPass: false,
  current_permission: "",
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(SET_CURRENT_USER, (state, action: PayloadAction<AuthState["userconnected"], typeof SET_CURRENT_USER>) => {
        const isAuthenticated = !isEmpty(action.payload);
        return {
          ...state,
          isAuthenticated,
          userconnected: action.payload,
          verifPass: isAuthenticated ? state.verifPass : false,
        };
      })
      .addCase(VERIF_PASS, (state, action: PayloadAction<unknown, typeof VERIF_PASS>) => ({
        ...state,
        verifPass: action.payload === true,
      }))
      .addCase(CURRENT_PERMISSION, (state, action: PayloadAction<CurrentPermissionPayload, typeof CURRENT_PERMISSION>) => {
        const permissions = Array.isArray(action.payload?.permissions)
          ? action.payload.permissions
          : [];
        const siteId = action.payload?.site_id;
        // SCRUM-856: normalize a no-match to null instead of leaking `undefined`
        // (a missing permission for the site is a real state, not "not yet loaded").
        const current_permission =
          permissions.find((perm) => perm.siteId === siteId) ?? null;
        return { ...state, current_permission };
      })
      .addCase(PROFILE_LOADING, (state) => ({
        ...state,
        loading: true,
      }))
      .addCase(GET_PROFILE, (state, action: PayloadAction<ProfileResponse, typeof GET_PROFILE>) => ({
        ...state,
        profile: action.payload,
        loading: false,
      }))
      .addCase(UPDATING_PASS, (state, action: PayloadAction<AuthState["user"], typeof UPDATING_PASS>) => ({
        ...state,
        user: action.payload,
      }));
  },
});

export default authSlice.reducer;
